import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import {
  SectionContainer,
  ChartTextContainer,
  ChartDescriptionContainer,
  StatisticsContainer,
} from "../common/Charts.styles";
import { AssessmentsSectionTitle } from "./AssessmentAnalytics.styles";
import { Creators as AllCreators } from "../../../../actions";
import { AssessmentChartFilter, AssessmentChartFilterLabels, AssessmentResult } from "../../../../App/Analytics/Common";
import { analyticsCourseAssessmentTotals } from "../../../../parsers/analytics";
import colors from "../../../../styles/colors";
import { getPercentage } from "../../../../utils";
import Span from "../../../utils/AnalyticsSpan";
import MoreButton, { More, CloseHelpTextButton } from "../common/MoreButton";
import AssessmentsGraph, { IAssessmentTotals } from "./AssessmentsGraph";
import { AssessmentsIcon } from "../AnalyticsIcons";
import { AnimatedUnmountWrapper } from "../../../utils/AnimationUnmountWrapper/AnimationUnmountWrapper";

const Creators = (AllCreators as any).App.ClassAnalytics;

export interface IAssessmentsOwnProps {
  selectedFilters: AssessmentChartFilterLabels[];
  assessments: AssessmentResult[][];
  chartFilter: AssessmentChartFilter;
  courseDisplayName: string;
  objectDisplayName: string;
}

interface IAssessmentsDispatchProps {
  exportAssessments: (assessmentsTotal: IAssessmentTotals, isXLSX: boolean) => void;
}

export type IAssessmentsProps = IAssessmentsOwnProps & IAssessmentsDispatchProps;
export interface IAssessmentsGraphState {
  showMore: boolean;
}

export class AssessmentsAnalytics extends React.PureComponent<IAssessmentsProps, IAssessmentsGraphState> {
  constructor(props: IAssessmentsProps) {
    super(props);
    this.state = { showMore: false };
  }

  public render() {
    const { assessments, chartFilter, selectedFilters } = this.props;
    const assessmentTotals = analyticsCourseAssessmentTotals(assessments);
    const {
      totalAssessments,
      totalPreAssigmentsNotTaken,
      totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
      totalPreAssigmentsTaken,
      totalPreAssigmentsTakenPassedOnFirst,
    } = assessmentTotals;
    const exportAssessmentsCSV = () => this.props.exportAssessments(assessmentTotals, false);
    const exportAssessmentsXLSX = () => this.props.exportAssessments(assessmentTotals, true);

    return (
      <SectionContainer>
        <StatisticsContainer>
          <ChartTextContainer>
            <AssessmentsSectionTitle>
              <AssessmentsIcon /> Assessments
            </AssessmentsSectionTitle>
            <ChartDescriptionContainer>
              <div>
                Learners were given a total of <Span color={colors.chartLightBlue100}>{totalAssessments}</Span> choices
                to take a Pre-Assessment or to step through a Lesson first.
                <br />
                <br />
                They decided to take one in{" "}
                <Span color={colors.chartDarkBlue100}>
                  {getPercentage(totalPreAssigmentsTaken, totalAssessments)}%
                </Span>{" "}
                of these cases and passed{" "}
                <Span color={colors.chartGreen100}>
                  {getPercentage(totalPreAssigmentsTakenPassedOnFirst, totalPreAssigmentsTaken)}%
                </Span>{" "}
                of those.
                <br />
                <br />
                In contrast, of the remaining{" "}
                <Span color={colors.chartDarkBlue100}>
                  {getPercentage(totalPreAssigmentsNotTaken, totalAssessments)}%
                </Span>{" "}
                of cases of Learners who took the Lesson first,{" "}
                <Span color={colors.chartGreen100}>
                  {getPercentage(totalPreAssigmentsNotTakenPassedOnFirstPostAssessment, totalPreAssigmentsNotTaken)}%
                </Span>{" "}
                passed their first Post-Assessment.
              </div>
              <MoreButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            </ChartDescriptionContainer>
          </ChartTextContainer>
          <AssessmentsGraph
            assessmentTotals={assessmentTotals}
            filterFunction={chartFilter}
            selectedFilters={selectedFilters}
            downloadCSV={exportAssessmentsCSV}
            downloadXLSX={exportAssessmentsXLSX}
          />
        </StatisticsContainer>
        <AnimatedUnmountWrapper isMounted={this.state.showMore} delayTime={300}>
          <More>
            <CloseHelpTextButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            <p>
              This chart provides an overview of whether Learners utilised Pre-Assessments and how those who did
              performed compared to those who did not.
            </p>
            <p>The higher the bottom horizontal bar, the more Pre-Assessments were taken.</p>
            <p>
              The top bar describes performance on first Post-Assessments for Learners who did not take a
              Pre-Assessment.
            </p>
            <p>
              Inside each bar, the width of the area indicates the proportion of attempts that were passed, failed or
              aborted.
            </p>
          </More>
        </AnimatedUnmountWrapper>
      </SectionContainer>
    );
  }

  public onToggleShowMore = () => {
    this.setState({ showMore: !this.state.showMore });
  };
}

export default connect<{}, IAssessmentsDispatchProps, IAssessmentsOwnProps, {}>(
  (state: {}, ownProps: IAssessmentsOwnProps) => ({}),
  (dispatch: Dispatch<IAction>, ownProps: IAssessmentsOwnProps) => ({
    exportAssessments: (assessmentsTotal, isXLSX) => {
      dispatch(
        Creators.exportAssessments(assessmentsTotal, ownProps.objectDisplayName, ownProps.courseDisplayName, isXLSX)
      );
    },
  })
)(AssessmentsAnalytics);
